/**
 * HmProductNew — założenie towaru w Symfonii.
 *
 * Formularz pokazuje TRZY pola wymagane (kod, nazwa, stawka VAT) na wierzchu, a resztę jako
 * opcjonalne. Tak wygląda prawda o tej trasie: sprawdzone odmowami na żywej instancji, Symfonia
 * nie potrzebuje niczego więcej, a dosyłanie pustych wartości „dla kompletu" nadpisałoby jej
 * własne wartości domyślne.
 *
 * KOD JEST NIEODWRACALNY: WebAPI nie ma kasowania towarów, a zmiana kodu to założenie kolejnego.
 * Stąd ostrzeżenie przy polu, a nie dopiero w komunikacie po zapisie.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { HmProductCreateInput, HmProductDetail, HmProductDictionaries } from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Save } from 'lucide-react';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Section } from '@/components/handel-magazyn/detail-parts';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { useActiveCompanyScope } from '@/lib/use-active-company';

const TYPES = [0, 1, 2, 3] as const;

interface FormState {
  code: string;
  name: string;
  vatRateCode: string;
  type: number;
  unit: string;
  kindCode: string;
  pkwiu: string;
  cn: string;
  barcode: string;
  fiscalName: string;
  note: string;
  minState: string;
  maxState: string;
  vat50Minus: boolean;
  reverseCharge: boolean;
  splitPayment: boolean;
}

const EMPTY: FormState = {
  code: '',
  name: '',
  vatRateCode: '',
  type: 0,
  unit: 'szt',
  kindCode: '',
  pkwiu: '',
  cn: '',
  barcode: '',
  fiscalName: '',
  note: '',
  minState: '',
  maxState: '',
  vat50Minus: false,
  reverseCharge: false,
  splitPayment: false,
};

/** Pola puste POMIJAMY — Symfonia ma dla nich własne wartości domyślne (patrz nagłówek pliku). */
function toPayload(form: FormState): HmProductCreateInput {
  const text = (value: string) => (value.trim() === '' ? undefined : value.trim());
  const num = (value: string) => (value.trim() === '' ? undefined : Number(value));
  return {
    code: form.code.trim(),
    name: form.name.trim(),
    vatRateCode: form.vatRateCode,
    type: form.type,
    ...(text(form.unit) ? { unit: text(form.unit) } : {}),
    ...(text(form.kindCode) ? { kindCode: text(form.kindCode) } : {}),
    ...(text(form.pkwiu) ? { pkwiu: text(form.pkwiu) } : {}),
    ...(text(form.cn) ? { cn: text(form.cn) } : {}),
    ...(text(form.barcode) ? { barcode: text(form.barcode) } : {}),
    ...(text(form.fiscalName) ? { fiscalName: text(form.fiscalName) } : {}),
    ...(text(form.note) ? { note: text(form.note) } : {}),
    ...(num(form.minState) != null ? { minState: num(form.minState) } : {}),
    ...(num(form.maxState) != null ? { maxState: num(form.maxState) } : {}),
    ...(form.vat50Minus ? { vat50Minus: true } : {}),
    ...(form.reverseCharge ? { reverseCharge: true } : {}),
    ...(form.splitPayment ? { splitPayment: true } : {}),
  };
}

export function HmProductNew() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const companyId = useActiveCompanyScope().companyId;

  const [form, setForm] = useState<FormState>(EMPTY);

  function set(patch: Partial<FormState>) {
    setForm((prev) => ({ ...prev, ...patch }));
  }

  const dictionaries = useQuery({
    queryKey: ['handel-magazyn', 'product-dictionaries', companyId],
    enabled: !!companyId,
    retry: false,
    queryFn: async () =>
      (
        await api.get<HmProductDictionaries>(
          `/handel-magazyn/product-dictionaries?companyId=${companyId}`,
        )
      ).data,
  });

  const mutation = useMutation({
    mutationFn: async () =>
      (
        await api.post<HmProductDetail>(
          `/handel-magazyn/products?companyId=${companyId}`,
          toPayload(form),
        )
      ).data,
    onSuccess: (product) => {
      void queryClient.invalidateQueries({ queryKey: ['handel-magazyn', 'products'] });
      toast.show({
        variant: 'success',
        title: t('tenant.handelMagazyn.newProduct.toast.created', { code: product.code }),
      });
      navigate(`/handel-magazyn/towary/${product.id}`);
    },
    onError: (err) => {
      const data = isAxiosError(err)
        ? (err.response?.data as
            | {
                message?: string;
                details?: { body?: string };
                fieldErrors?: { field: string; message: string }[];
                // Zod zwraca błędy per pole w innym kształcie niż nasza własna walidacja.
                details2?: unknown;
              }
            | undefined)
        : undefined;
      const zod = isAxiosError(err)
        ? (
            err.response?.data as
              | { details?: { fieldErrors?: Record<string, string[]> } }
              | undefined
          )?.details?.fieldErrors
        : undefined;
      const fromZod = zod ? Object.values(zod).flat().join(' · ') : '';
      const fromOurs = (data?.fieldErrors ?? []).map((e) => e.message).join(' · ');
      toast.show({
        variant: 'error',
        title: t('tenant.handelMagazyn.newProduct.toast.failed'),
        description: fromOurs || fromZod || data?.details?.body || data?.message || undefined,
      });
    },
  });

  const canSubmit =
    !!companyId &&
    form.code.trim() !== '' &&
    form.name.trim() !== '' &&
    form.vatRateCode !== '' &&
    !mutation.isPending;

  return (
    <div className="p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-text-primary">
            {t('tenant.handelMagazyn.newProduct.title')}
          </h1>
          <p className="text-sm text-text-secondary">
            {t('tenant.handelMagazyn.newProduct.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => navigate('/handel-magazyn/towary')}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Save className="h-4 w-4" />}
            loading={mutation.isPending}
            disabled={!canSubmit}
            onClick={() => mutation.mutate()}
            data-testid="hm-product-new-save"
          >
            {t('tenant.handelMagazyn.newProduct.save')}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Section title={t('tenant.handelMagazyn.newProduct.required')}>
          <Input
            label={t('tenant.handelMagazyn.products.columns.code')}
            value={form.code}
            onChange={(e) => set({ code: e.target.value })}
            hint={t('tenant.handelMagazyn.newProduct.codeHint')}
            required
            data-testid="hm-product-new-code"
          />
          <Input
            label={t('tenant.handelMagazyn.products.columns.name')}
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
            required
            data-testid="hm-product-new-name"
          />
          <Select
            label={t('tenant.handelMagazyn.columns.vat')}
            value={form.vatRateCode}
            onChange={(e) => set({ vatRateCode: e.target.value })}
            placeholder={t('tenant.handelMagazyn.newSale.choose')}
            required
            data-testid="hm-product-new-vat"
            options={(dictionaries.data?.vatRates ?? []).map((rate) => ({
              value: rate.code,
              label: rate.description ? `${rate.code} — ${rate.description}` : rate.code,
            }))}
          />
          <Select
            label={t('tenant.handelMagazyn.products.columns.type')}
            value={String(form.type)}
            onChange={(e) => set({ type: Number(e.target.value) })}
            options={TYPES.map((type) => ({
              value: String(type),
              label: t(`tenant.handelMagazyn.newProduct.type.${type}`),
            }))}
          />
          <Input
            label={t('tenant.handelMagazyn.products.columns.unit')}
            value={form.unit}
            onChange={(e) => set({ unit: e.target.value })}
            hint={t('tenant.handelMagazyn.newProduct.unitHint')}
          />
          {dictionaries.isError && (
            <p className="text-xs text-amber-700">
              {t('tenant.handelMagazyn.newProduct.dictionariesFailed')}
            </p>
          )}
        </Section>

        <Section title={t('tenant.handelMagazyn.newProduct.optional')}>
          <div className="grid grid-cols-2 gap-2">
            <Input
              label={t('tenant.handelMagazyn.products.columns.pkwiu')}
              value={form.pkwiu}
              onChange={(e) => set({ pkwiu: e.target.value })}
            />
            <Input
              label={t('tenant.handelMagazyn.products.columns.cn')}
              value={form.cn}
              onChange={(e) => set({ cn: e.target.value })}
            />
          </div>
          <Input
            label={t('tenant.handelMagazyn.products.columns.barcode')}
            value={form.barcode}
            onChange={(e) => set({ barcode: e.target.value })}
          />
          <Input
            label={t('tenant.handelMagazyn.products.fiscalName')}
            value={form.fiscalName}
            onChange={(e) => set({ fiscalName: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              label={t('tenant.handelMagazyn.products.minState')}
              value={form.minState}
              onChange={(e) => set({ minState: e.target.value })}
              inputMode="decimal"
            />
            <Input
              label={t('tenant.handelMagazyn.products.maxState')}
              value={form.maxState}
              onChange={(e) => set({ maxState: e.target.value })}
              inputMode="decimal"
            />
          </div>
          <Input
            label={t('tenant.handelMagazyn.products.note')}
            value={form.note}
            onChange={(e) => set({ note: e.target.value })}
          />
          <Checkbox
            checked={form.vat50Minus}
            onChange={(e) => set({ vat50Minus: e.target.checked })}
            label={t('tenant.handelMagazyn.products.vat50')}
          />
          <Checkbox
            checked={form.reverseCharge}
            onChange={(e) => set({ reverseCharge: e.target.checked })}
            label={t('tenant.handelMagazyn.products.reverseCharge')}
          />
          <Checkbox
            checked={form.splitPayment}
            onChange={(e) => set({ splitPayment: e.target.checked })}
            label={t('tenant.handelMagazyn.products.splitPayment')}
          />
        </Section>
      </div>
    </div>
  );
}

export default HmProductNew;
